// pages/OwnerAgreements.js
// Dedicated owner page for agreement template management.
// Route: /owner/agreements[?company_id=...]
//
// Template upload/parse/mapping/approval is platform-owner-only (7 July 2026
// — see broker_companies.introducer_agreement_workflow_enabled). The owner
// picks which broker company they're managing, since the platform owner's
// own account isn't tied to a single company.

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import AgreementTemplateManager from '../components/AgreementTemplateManager';

const token = () => localStorage.getItem('token');
const API = process.env.REACT_APP_API_URL || 'https://brokerconnect.co.uk/api';

export default function OwnerAgreements() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const companyId = searchParams.get('company_id') || '';

  const loadCompanies = useCallback(async () => {
    try {
      const res = await fetch(`${API}/owner/companies`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      const list = Array.isArray(data.companies) ? data.companies : [];
      setCompanies(list);
      // Auto-select if there's only one company and none picked yet.
      if (!companyId && list.length === 1) {
        setSearchParams({ company_id: list[0].id });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [companyId, setSearchParams]);

  useEffect(() => { loadCompanies(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = companies.find(c => c.id === companyId);

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#F7FAFC' }}>
      <Sidebar />
      <main className="main-content" style={{ padding:'32px 40px', maxWidth:800 }}>
        <div style={{ marginBottom:20 }}>
          <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#4b5a6e', marginBottom:6 }}>
            Broker company
          </label>
          <select
            value={companyId}
            onChange={e => setSearchParams(e.target.value ? { company_id: e.target.value } : {})}
            disabled={loading}
            style={{
              width:'100%', maxWidth:360, boxSizing:'border-box',
              padding:'10px 12px', borderRadius:8,
              border:'1px solid #e2e8f0', fontSize:13,
              color:'#2d3748', background:'#fff', outline:'none',
            }}
          >
            <option value="">
              {loading ? 'Loading companies…' : '— Select a company —'}
            </option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}{c.active_agreement_template_name ? ` · live: ${c.active_agreement_template_name}` : ''}
              </option>
            ))}
          </select>
          {selected && (
            <p style={{ margin:'8px 0 0', fontSize:12, color: selected.active_agreement_template_id ? '#166534' : '#94a3b8' }}>
              {selected.active_agreement_template_id
                ? `✓ "${selected.active_agreement_template_name}" is live — brokers here get this agreement at invite time.`
                : 'Nothing is live for this company yet — approve a template below, then push it live from its row.'}
            </p>
          )}
        </div>

        <AgreementTemplateManager
          token={token()}
          companyId={companyId || null}
          companyName={selected?.name}
          activeTemplateId={selected?.active_agreement_template_id || null}
          onSetLive={loadCompanies}
        />
      </main>
    </div>
  );
}
